import { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';
import { 
    Shield, 
    Lock, 
    ArrowLeft,
    Plus,
    Check,
    X
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../contexts/ToastContext';

interface Permission {
    id: string;
    code: string;
    resource: string;
    action: string;
    description: string;
}

interface Role {
    id: string;
    code: string;
    name: string;
    description: string;
    isSystem: boolean;
    permissions: Permission[];
}

function RoleManagement({ onBack }: { onBack: () => void }) {
    const { addToast } = useToast();
    const [roles, setRoles] = useState<Role[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isAddingRole, setIsAddingRole] = useState(false);
    const [newRole, setNewRole] = useState({ code: '', name: '', description: '' });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [rolesData, permsData] = await Promise.all([
                adminApi.getRoles(),
                adminApi.getPermissions()
            ]);
            setRoles(rolesData);
            setPermissions(permsData);
        } catch (error) {
            addToast('error', 'Error al cargar datos de seguridad');
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdatePermissions = async (roleId: string, currentPerms: Permission[]) => {
        setIsSaving(true);
        try {
            const permissionIds = currentPerms.map(p => p.id);
            await adminApi.updateRolePermissions(roleId, permissionIds);
            addToast('success', 'Permisos actualizados correctamente');
            fetchData();
        } catch (error) {
            addToast('error', 'Error al guardar cambios');
        } finally {
            setIsSaving(false);
        }
    };

    const togglePermission = (role: Role, permission: Permission) => {
        const hasPerm = role.permissions.some(p => p.id === permission.id);
        const nextPerms = hasPerm 
            ? role.permissions.filter(p => p.id !== permission.id)
            : [...role.permissions, permission];
        
        // Update local state temporarily
        setRoles(roles.map(r => r.id === role.id ? { ...r, permissions: nextPerms } : r));
    };

    const handleCreateRole = async () => {
        if (!newRole.code || !newRole.name) return;
        setIsSaving(true);
        try {
            await adminApi.createRole(newRole);
            addToast('success', 'Rol creado exitosamente');
            setNewRole({ code: '', name: '', description: '' });
            setIsAddingRole(false);
            fetchData();
        } catch (error) {
            addToast('error', 'Error al crear rol');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center p-20 animate-pulse">
                <Shield className="text-teal-500/20 mb-4" size={48} />
                <p className="text-gray-500 font-mono text-xs uppercase tracking-widest">Sincronizando Matriz de Seguridad...</p>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header */}
            <div className="flex justify-between items-center">
                <button 
                    onClick={onBack}
                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
                >
                    <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                    <span>Volver al Panel</span>
                </button>
                <Button 
                    className="bg-teal-500 hover:bg-teal-600 text-black font-bold flex items-center gap-2"
                    onClick={() => setIsAddingRole(true)}
                >
                    <Plus size={18} />
                    Crear Nuevo Rol
                </Button>
            </div>

            <main className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Roles List */}
                <div className="lg:col-span-1 space-y-4">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 px-2">Roles del Sistema</h3>
                    {roles.map(role => (
                        <button
                            key={role.id}
                            onClick={() => setSelectedRole(role)}
                            className={`w-full text-left p-4 rounded-2xl border transition-all ${
                                selectedRole?.id === role.id 
                                ? 'bg-teal-500/10 border-teal-500 text-white shadow-lg shadow-teal-500/10' 
                                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:border-white/20'
                            }`}
                        >
                            <div className="flex items-center gap-3 mb-1">
                                <Shield size={16} className={selectedRole?.id === role.id ? 'text-teal-400' : 'text-gray-500'} />
                                <span className="font-bold text-sm">{role.code}</span>
                            </div>
                            <p className="text-[10px] opacity-60 leading-tight">{role.description}</p>
                            {role.isSystem && (
                                <span className="mt-2 inline-block text-[8px] bg-white/10 px-1.5 py-0.5 rounded text-gray-300 uppercase font-bold tracking-tighter">System Default</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Permissions Matrix */}
                <div className="lg:col-span-3">
                    {selectedRole ? (
                        <div className="bg-white/5 border border-white/10 rounded-[32px] overflow-hidden flex flex-col h-full">
                            <div className="p-8 border-b border-white/10 flex justify-between items-center bg-white/[0.02]">
                                <div>
                                    <h2 className="text-2xl font-bold flex items-center gap-3">
                                        {selectedRole.name}
                                        <span className="text-xs font-mono text-teal-400 bg-teal-400/10 px-2 py-1 rounded">
                                            {selectedRole.permissions.length} Permisos Activos
                                        </span>
                                    </h2>
                                    <p className="text-sm text-gray-400 mt-1">{selectedRole.description}</p>
                                </div>
                                <Button 
                                    className="bg-white text-black font-bold h-10 px-6"
                                    onClick={() => handleUpdatePermissions(selectedRole.id, roles.find(r => r.id === selectedRole.id)!.permissions)}
                                    isLoading={isSaving}
                                >
                                    Guardar Cambios
                                </Button>
                            </div>

                            <div className="p-8 overflow-y-auto max-h-[600px] custom-scrollbar">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {permissions.map(perm => {
                                        const isChecked = roles.find(r => r.id === selectedRole.id)?.permissions.some(p => p.id === perm.id);
                                        return (
                                            <button
                                                key={perm.id}
                                                onClick={() => togglePermission(roles.find(r => r.id === selectedRole.id)!, perm)}
                                                className={`p-4 rounded-2xl border text-left transition-all flex items-start gap-4 ${
                                                    isChecked 
                                                    ? 'bg-emerald-500/10 border-emerald-500/30' 
                                                    : 'bg-white/5 border-white/5 hover:border-white/10'
                                                }`}
                                            >
                                                <div className={`mt-1 w-5 h-5 rounded-lg border flex items-center justify-center transition-all ${
                                                    isChecked 
                                                    ? 'bg-emerald-500 border-emerald-500 text-black' 
                                                    : 'bg-transparent border-white/20 text-transparent'
                                                }`}>
                                                    <Check size={12} strokeWidth={4} />
                                                </div>
                                                <div>
                                                    <p className={`text-xs font-mono font-bold mb-1 ${isChecked ? 'text-emerald-400' : 'text-gray-400'}`}>
                                                        {perm.code}
                                                    </p>
                                                    <p className="text-[10px] text-gray-500 leading-snug">{perm.description}</p>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="h-full bg-white/5 border border-white/10 border-dashed rounded-[32px] flex flex-col items-center justify-center p-20 text-center">
                            <Lock className="text-gray-700 mb-6" size={64} />
                            <h3 className="text-xl font-bold text-gray-300">Selecciona un Rol</h3>
                            <p className="text-sm text-gray-500 max-w-xs mt-2">
                                Haz clic en un rol de la izquierda para gestionar sus permisos granulares y definir qué acciones puede realizar en la plataforma.
                            </p>
                        </div>
                    )}
                </div>
            </main>

            {/* Create Role Modal placeholder */}
            {isAddingRole && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 backdrop-blur-xl bg-black/60 scale-in">
                    <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-8 rounded-[40px] shadow-2xl relative">
                        <button 
                            className="absolute top-6 right-6 text-gray-500 hover:text-white"
                            onClick={() => setIsAddingRole(false)}
                        >
                            <X size={24} />
                        </button>

                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-12 h-12 bg-teal-500/20 rounded-2xl flex items-center justify-center text-teal-400">
                                <Plus size={24} />
                            </div>
                            <h2 className="text-2xl font-bold tracking-tight">Nuevo Rol Global</h2>
                        </div>

                        <div className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Code (Slug)</label>
                                <Input 
                                    className="bg-white/5 border-white/10 h-12"
                                    placeholder="ej. STAFF_MANAGER"
                                    value={newRole.code}
                                    onChange={e => setNewRole({...newRole, code: e.target.value.toUpperCase()})}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Nombre Visible</label>
                                <Input 
                                    className="bg-white/5 border-white/10 h-12"
                                    placeholder="ej. Gerente de Equipo"
                                    value={newRole.name}
                                    onChange={e => setNewRole({...newRole, name: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Descripción</label>
                                <textarea 
                                    className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-sm focus:ring-1 focus:ring-teal-500 outline-none h-24"
                                    placeholder="Define las responsabilidades de este rol..."
                                    value={newRole.description}
                                    onChange={e => setNewRole({...newRole, description: e.target.value})}
                                />
                            </div>

                            <Button 
                                fullWidth 
                                className="bg-white text-black font-bold h-14 rounded-2xl mt-4"
                                onClick={handleCreateRole}
                                isLoading={isSaving}
                            >
                                Crear Rol
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RoleManagement;
